import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import PlusIcon from "@material-ui/icons/Add";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import LockIcon from "@material-ui/icons/Lock";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import clsx from "clsx";
import { FormikProps } from "formik";
import { ReactNode } from "react";
import * as yup from "yup";
import { Product } from "../../../../api";
import { usePrimarySatProduct } from "../../../../common/helpers";
import { ProductStyles, useSharedStyles } from "../../../../common/sharedStyles";
import { withdrawTimeframes, YesNoOption } from "../../../../common/types";
import { ReactComponent as PadlockClosedIcon } from "../../../../images/padlock-closed.svg";
import { ReactComponent as PadlockOpenIcon } from "../../../../images/padlock-open.svg";
import { useAppSelector } from "../../../../store";
import { Colors, SubtitleText, TitleText } from "../../../controls/helpers";
import { Values } from "../../ControlledForm";
import { Body, Hero } from "../../GridLayout";
import QuestionnaireScreen, { deserializeResponses, serializeResponses } from "../../QuestionnaireScreen";
import Select from "../../Select";
import { useEmployed } from "./BeginQuestions";

const planToWithdrawQuestionField = "planToWithdrawWealthBuilder";
const planToWithdrawQuestion = "Are you planning to withdraw your Wealth Builder investment in the next 5 years?";
const timeFrameQuestion = "Approximately when do you plan to make a withdrawal in WealthBuilder?";
const timeFrameQuestionFieldName = "timeframe";

export const usePlanningToWithdrawWb = () => {
  const { responses } = useAppSelector((state) => state.clientQuestionnaires);
  const { planToWithdrawWealthBuilder } = deserializeResponses({
    [planToWithdrawQuestionField]: planToWithdrawQuestion,
    [timeFrameQuestionFieldName]: timeFrameQuestion,
  })(responses || {});
  return planToWithdrawWealthBuilder === YesNoOption.Yes;
};

export const useWbWithdrawTimeframe = () => {
  const { responses } = useAppSelector((state) => state.clientQuestionnaires);
  const { timeframe } = deserializeResponses({
    [planToWithdrawQuestionField]: planToWithdrawQuestion,
    [timeFrameQuestionFieldName]: timeFrameQuestion,
  })(responses || {});
  return (timeframe || "") as string;
};

const serializeWithdrawResponses = (values: Values): Record<string, string> => {
  const responses = serializeResponses({
    [planToWithdrawQuestionField]: planToWithdrawQuestion,
    [timeFrameQuestionFieldName]: timeFrameQuestion,
  })(values);
  // Make sure we don't send response to withdraw timeframe question if planToWithdraw === 'No'
  if (responses[planToWithdrawQuestion] && responses[planToWithdrawQuestion] === YesNoOption.No) return { [planToWithdrawQuestion]: responses[planToWithdrawQuestion] };
  else return responses;
};

const WealthBuilder = () => {
  const classes = useStyles();
  const sharedClasses = useSharedStyles();
  const primaryProduct = usePrimarySatProduct();
  const employed = useEmployed();

  return (
    <QuestionnaireScreen
      initialValues={{ [planToWithdrawQuestionField]: "", [timeFrameQuestionFieldName]: "" }}
      validationSchema={validationSchema}
      serializeResponses={serializeWithdrawResponses}
      deserializeResponses={deserializeResponses({
        [planToWithdrawQuestionField]: planToWithdrawQuestion,
        [timeFrameQuestionFieldName]: timeFrameQuestion,
      })}
      overrideNextPageRoute={(values) => (values.planToWithdrawWealthBuilder === YesNoOption.Yes ? "wb-self-select" : "wb-life-cycle")}
    >
      {(formik: FormikProps<any>) => (
        <>
          <Hero isNarrowView className={classes.hero}>
            <TitleText>Use Wealth Builder to unlock your savings.</TitleText>
          </Hero>
          <Body isNarrowView>
            <TitleText>Typically Kiwis need to save 9% of their income to achieve their retirement goals.</TitleText>
            <Box py={2}>
              {employed && (
                <>
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Box display="flex" justifyContent="center" alignItems="center">
                      <ProductBox title="KiwiSaver" subtitle="Employer contribution*" percent="3" color="#0C7A28" icon={<PadlockClosedIcon className={classes.icon} />} />
                      <Typography className={classes.symbol}>+</Typography>
                      <ProductBox title="KiwiSaver" subtitle="Your contribution" percent="3" color={ProductStyles.KiwiSaver.color} icon={<PadlockClosedIcon className={classes.icon} />} />
                      <Typography className={classes.symbol}>+</Typography>
                      <ProductBox title="Wealth Builder" subtitle="Your new contribution" percent="3" color={ProductStyles.WealthBuilder.color} icon={<PadlockOpenIcon className={classes.icon} />} />
                    </Box>
                    <Box pt={2} mb={-1.5}>
                      <Typography className={classes.symbol}>=</Typography>
                    </Box>
                    <ProductBox title="Total Savings" subtitle="To ace retirement" percent="9" color="#0053F3" icon={<CheckCircleIcon style={{ transform: "scale(1.11)" }} />} />
                  </Box>
                  <Box mt={6} textAlign="center">
                    <Typography className={sharedClasses.smallPrint}>*Before Employer Superannuation Contribution Tax (ESCT) is deducted.</Typography>
                  </Box>
                </>
              )}
              {!employed && (
                <>
                  <Typography variant="h6" style={{ color: Colors.Blue }}>
                    For the self-employed
                  </Typography>
                  <Box pt={2} display="flex" flexDirection="column" alignItems="center">
                    <Box display="flex" justifyContent="center" alignItems="center">
                      <Grid container>
                        <Grid item sm={3} xs={12}>
                          <SelfEmployedProductBox
                            title="$1,042"
                            subtitle="Minimum annual KiwiSaver contribution"
                            percent="Locked in till 65"
                            color={ProductStyles.KiwiSaver.color}
                            icon={<LockIcon className={clsx(classes.lockIcon, sharedClasses.black)} />}
                          />
                        </Grid>
                        <Grid item sm xs={12} style={{ display: "table" }}>
                          <PlusBox />
                        </Grid>
                        <Grid item sm={3} xs={12}>
                          <SelfEmployedProductBox
                            title="+$521"
                            subtitle="Annual government contribution"
                            percent="Locked in till 65"
                            color={ProductStyles.KiwiSaver.color}
                            icon={<LockIcon className={clsx(classes.lockIcon, sharedClasses.black)} />}
                          />
                        </Grid>
                        <Grid item sm xs={12} style={{ display: "table" }}>
                          <PlusBox />
                        </Grid>
                        <Grid item sm={3} xs={12}>
                          <SelfEmployedProductBox
                            title="3–9%"
                            subtitle="Another retirement savings solution"
                            percent="Not locked in"
                            color={ProductStyles.WealthBuilder.color}
                            unlocked
                            icon={<LockOpenIcon className={classes.lockIcon} />}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
            <Box py={2}>
              <SubtitleText>
                Wealth Builder is just like KiwiSaver but without the lock-in. Use it to boost your savings contribution rate{primaryProduct !== Product.WealthBuilder && " to 9%"}. With Wealth Builder
                you can access your money whenever you need it.
              </SubtitleText>
            </Box>
            <Grid container spacing={3}>
              <Grid item sm={8} xs={12}>
                <SubtitleText hideMarginBottom>{planToWithdrawQuestion}</SubtitleText>
              </Grid>
              <Grid item sm={4} xs={12}>
                <Select noBlankOption fullWidth name={planToWithdrawQuestionField} options={[YesNoOption.Yes, YesNoOption.No]} />
              </Grid>
              {formik.values[planToWithdrawQuestionField] === YesNoOption.Yes && (
                <>
                  <Grid item sm={8} xs={12}>
                    <SubtitleText hideMarginBottom>{timeFrameQuestion}</SubtitleText>
                  </Grid>
                  <Grid item sm={4} xs={12}>
                    <Select noBlankOption fullWidth name={timeFrameQuestionFieldName} options={withdrawTimeframes.map((t) => t.label)} />
                  </Grid>
                </>
              )}
            </Grid>
            <br />
          </Body>
        </>
      )}
    </QuestionnaireScreen>
  );
};

const validationSchema = yup.object().shape({
  [planToWithdrawQuestionField]: yup.string().required("A response is required"),
  [timeFrameQuestionFieldName]: yup.string().when([planToWithdrawQuestionField], {
    is: (mainQuestionResponse: string) => mainQuestionResponse === YesNoOption.Yes,
    then: yup.string().required("A response is required"),
    otherwise: yup.string(),
  }),
});

interface ProductBoxProps {
  title: string;
  percent: string;
  color: string;
  subtitle: string;
  icon: ReactNode;
}

const ProductBox = ({ title, percent, color, subtitle, icon }: ProductBoxProps) => {
  const classes = useStylesProductBox({ color });
  const sharedClasses = useSharedStyles();
  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Box className={classes.root}>
        {title && <Typography className={clsx(classes.title, sharedClasses.white)}>{title}</Typography>}
        <Box display="flex" justifyContent="center" alignItems="center" className={sharedClasses.white}>
          {icon}
          <Typography className={classes.percent}>{percent}%</Typography>
        </Box>
      </Box>
      <Typography className={classes.subtitle}>{subtitle}</Typography>
    </Box>
  );
};

const SelfEmployedProductBox = ({ title, percent, color, subtitle, icon, unlocked }: ProductBoxProps & { unlocked?: boolean }) => {
  const sharedClasses = useSharedStyles();
  return (
    <Box height={300} bgcolor={color} borderRadius={4} padding={2} position="relative">
      <Typography gutterBottom={false} variant="h4" className={sharedClasses.white}>
        {title}
      </Typography>
      <Typography gutterBottom className={sharedClasses.white}>
        {subtitle}
      </Typography>
      <Box className={sharedClasses.white} position="absolute" bottom={10}>
        {icon}
        <br />
        <Typography className={unlocked ? sharedClasses.white : sharedClasses.black}>{percent}</Typography>
      </Box>
    </Box>
  );
};

const PlusBox = () => {
  const classes = useStyles();
  return (
    <Box height="auto" textAlign="center" display="table-cell" style={{ display: "table-cell", verticalAlign: "middle" }}>
      <PlusIcon className={classes.icon} style={{ margin: "50px auto" }} />
    </Box>
  );
};

const useStyles = makeStyles(
  {
    symbol: {
      fontSize: "1.11rem",
      fontWeight: 500,
      position: "relative",
      padding: "0 5px",
      top: -18,
    },
    icon: {
      transform: "scale(1.65)",
    },
    lockIcon: {
      transform: "scale(1.5)",
    },
    hero: {
      justifyContent: "flex-start !important",
      height: "144px !important",
      backgroundColor: `${ProductStyles.WealthBuilder.color} !important`,
      alignItems: "normal !important",
      padding: 24,
      "& h4": {
        color: `${Colors.White} !important`,
        lineHeight: 1,
      },
    },
  },
  { name: "WealthBuilder" }
);

const useStylesProductBox = makeStyles(
  {
    root: (props: { color: string }) => ({
      background: props.color,
      borderRadius: 4,
      width: 100,
      height: 67,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    }),
    percent: {
      fontSize: "1.72rem",
      textAlign: "center",
      fontWeight: 500,
      marginTop: -2,
      marginLeft: 8,
    },
    title: {
      fontSize: "0.9rem",
      textAlign: "center",
      fontWeight: 500,
      marginBottom: -2,
    },
    subtitle: {
      fontSize: "0.75rem",
      width: 60,
      textAlign: "center",
      marginTop: 3,
    },
  },
  { name: "WealthBuilderProduct" }
);

export default WealthBuilder;
